import type { SocialMediaLinks } from '@code-troopers/mobile-preview/types/grab/types'
import axios, { type AxiosResponse } from 'axios'

import {
  computeFakeTitle,
  eventsToDbFormat,
} from '@/pages/progEvent/progEvent.service'
import type {
  EditorGroup,
  EditorImage,
  GenreId,
  GroupId,
  GuestTypeId,
} from '@/shared/types/applicationData.type'
import { type DataOptions } from '@/shared/types/dataOptions.type'
import { type GuestDto } from '@/shared/types/musicGroup/guestDto.type'
import {
  type MusicGroup,
  type WebviewContact,
} from '@/shared/types/musicGroup/musicGroup.type'
import {
  type MusicGroupEventFormInput,
  musicGroupEventFormInputsToProgEvents,
} from '@/shared/types/musicGroup/musicGroupEvent.type'
import { type ProgEvent } from '@/shared/types/progEvent.type'

import { type GuestsState } from './store/guests.store'

// === Editor → Preview adapter ===
//
// Converts the backoffice's `MusicGroup` editor model into the editor-widened
// `EditorGroup` slice. Branded id types (`GroupId` / `GenreId` /
// `GuestTypeId`) are reapplied at assignment; the editor's wider
// `SocialLinks` map is single-step cast to mobile's `SocialMediaLinks`
// (keys align — only the value type was widened in the editor).
export type AdaptGuestInput = {
  guest: Partial<MusicGroup> | null | undefined
  croppedImage: string | null
  fileImage: string | undefined | null
  genreIds: string[]
  links: Partial<Record<string, string>>
}

export function adaptGuest({
  guest,
  croppedImage,
  fileImage,
  genreIds,
  links,
}: AdaptGuestInput): EditorGroup {
  const isDataUrl = (v: unknown): v is string =>
    typeof v === 'string' && (v.startsWith('data:') || v.startsWith('zdata:'))
  const sanitizeImageField = (
    v: EditorImage | string | undefined | null,
  ): EditorImage | undefined => {
    if (v == null) return undefined
    if (typeof v === 'string') return isDataUrl(v) ? undefined : v
    if (
      typeof v === 'object' &&
      'previewDataUri' in v &&
      isDataUrl((v as { previewDataUri: string }).previewDataUri)
    ) {
      return undefined
    }
    return v as EditorImage
  }
  const image: EditorImage | undefined =
    croppedImage != null
      ? { previewDataUri: croppedImage }
      : sanitizeImageField(fileImage ?? guest?.image ?? undefined)
  return {
    _id: (guest?._id ?? 'create') as GroupId,
    name: guest?.names?.fr ?? '',
    genres: genreIds as GenreId[],
    image,
    copyright: guest?.copyright ?? '',
    description: guest?.descriptions?.fr,
    links: links as SocialMediaLinks,
    typeId: (guest?.type ?? '') as GuestTypeId,
    phone: guest?.phone,
    email: guest?.email,
    contact: guest?.contact,
    weight: guest?.weight ?? undefined,
    lineUp: guest?.lineUp?.fr,
    location: guest?.location ?? undefined,
    address: guest?.address,
    webview:
      guest?.webview?.target != null && guest.webview.target !== ''
        ? {
            icon: guest.webview.icon,
            name: guest.webview.names.fr,
            target: guest.webview.target,
          }
        : undefined,
  }
}

function eventsToJsFormat(data: ProgEvent[]): ProgEvent[] {
  return data.map((element) => ({
    ...element,
    fakeTitle: computeFakeTitle(element),
  }))
}

function guestsToJsFormat(guests: MusicGroup[]): MusicGroup[] {
  return guests.map(guestToJsFormat)
}

function guestToDbFormat(guest: MusicGroup): MusicGroup {
  if (guest.location && !(guest.location.length > 0)) delete guest.location
  if (guest.webview?.target?.trim() === '') delete guest.webview
  return guest
}

function guestToJsFormat(g: MusicGroup): MusicGroup {
  const guest = g as Partial<MusicGroup>
  if (!guest.location) guest.location = []
  if (!guest.descriptions) guest.descriptions = { fr: '', en: '' }
  if (!guest.lineUp) guest.lineUp = { fr: '', en: '' }
  if (!guest.names) guest.names = { fr: '', en: '' }
  if (!guest.tags) guest.tags = []
  if (!guest.links) guest.links = {}
  if (!guest.webview) {
    guest.webview = {
      icon: 'fa-solid fa-calendar',
      names: { fr: '', en: '' },
      target: '',
    }
  } else {
    // a contact saved by an older version may have no names
    if ((guest.webview.names as WebviewContact['names'] | undefined) == null)
      guest.webview.names = { fr: '', en: '' }
    if (guest.webview.target === undefined) guest.webview.target = ''
  }

  // Ensure reactive fields for form
  if (guest.weight === undefined) guest.weight = null
  if (guest.copyright === undefined) guest.copyright = ''
  if (guest.contact === undefined) guest.contact = ''
  if (guest.email === undefined) guest.email = ''
  if (guest.address === undefined) guest.address = ''
  if (guest.phone === undefined) guest.phone = ''

  return guest as MusicGroup
}

export const GuestsService = {
  async getAll(): Promise<MusicGroup[]> {
    const { data } = await axios.get<MusicGroup[]>('/api/groups')
    return guestsToJsFormat(data)
  },

  async deleteGuest(id: string): Promise<void> {
    await axios.delete(`/api/groups/${id}`)
  },

  async deleteMultipleGuests(ids: string[]): Promise<void> {
    await axios.post(`/api/groups/batch-delete`, ids)
  },

  async addGuest(
    guest: MusicGroup,
    guestEvents: MusicGroupEventFormInput[],
  ): Promise<GuestDto> {
    return await axios.post('/api/groups', {
      ...guestToDbFormat(guest),
      newEvents: eventsToDbFormat(
        musicGroupEventFormInputsToProgEvents(guestEvents),
      ),
    })
  },

  async updateGuest(
    guest: MusicGroup,
    guestEvents: MusicGroupEventFormInput[] | undefined,
  ): Promise<GuestDto> {
    return await axios.put(`/api/groups/${guest._id}`, {
      ...guestToDbFormat(guest),
      newEvents: eventsToDbFormat(
        musicGroupEventFormInputsToProgEvents(guestEvents),
      ),
    })
  },

  async updateGuestWeight(id: string, weight: number | null): Promise<void> {
    await axios.put(`/api/groups/${id}/weight`, { weight })
  },

  async hideShowBatch(ids: string[], show: boolean): Promise<void> {
    await axios.post('/api/groups/batch-hide-show', { ids, show })
  },

  async getGuestById(guestId: string): Promise<GuestDto> {
    const { data } = await axios.get<MusicGroup>(`/api/groups/${guestId}`)
    return guestToJsFormat(data) as GuestDto
  },

  async getGuestsLinks(): Promise<string[]> {
    const { data } = await axios.get<string[]>('/api/groupsLinks')
    return data
  },

  async getGuestsLocations(): Promise<string[][]> {
    const { data } = await axios.get<string[][]>('/api/groupsLocationsList')
    return data
  },

  async getGuestsLight(): Promise<
    Pick<
      MusicGroup,
      | '_id'
      | 'names'
      | 'descriptions'
      | 'image'
      | 'copyright'
      | 'genres'
      | 'weight'
      | 'lineUp'
      | 'showArtist'
    >[]
  > {
    const { data } =
      await axios.get<
        Pick<
          MusicGroup,
          | '_id'
          | 'names'
          | 'descriptions'
          | 'image'
          | 'copyright'
          | 'genres'
          | 'weight'
          | 'lineUp'
          | 'showArtist'
        >[]
      >('/api/groupsLight')
    return data
  },
  async getPaginatedGuests(
    search: string,
    options: Partial<DataOptions>,
    offset: number,
    guestTypeId?: string,
  ): Promise<{
    musicGroups: MusicGroup[]
    total: number
    countByType: Record<string, number>
  }> {
    const params = {
      query: search,
      offset,
      limit: options.itemsPerPage,
    }
    const sortParams =
      options.sortBy?.[0] != null
        ? { order: options.sortBy[0], desc: options.sortDesc?.[0] }
        : {}
    const guestParams = guestTypeId != null ? { type: guestTypeId } : {}
    const { data } = await axios.get<{
      musicGroups: MusicGroup[]
      total: number
      countByType: Record<string, number>
    }>(`/api/groupsPaginated`, {
      params: { ...params, ...sortParams, ...guestParams },
    })
    data.musicGroups = guestsToJsFormat(data.musicGroups)
    return data
  },
  async uploadFile(
    file: string | ArrayBuffer | null,
    visible = false,
  ): Promise<AxiosResponse<string>> {
    return await axios.post<string>(
      '/api/groups/batch/importXls',
      { file },
      { params: { visible } },
    )
  },
  async getImportResult(batchId: string): Promise<
    AxiosResponse<{
      successes: { key: string; content: Record<string, string> }[]
      errors: { key: string; content: Record<string, string> }[]
      toProcess: { key: string; content: Record<string, string> }[]
    }>
  > {
    return await axios.get<Exclude<GuestsState['importFeedback'], undefined>>(
      `/api/groups/batch/importResult/${batchId}`,
    )
  },

  async getGuestEvents(guestIds: string[]): Promise<ProgEvent[]> {
    const { data } = await axios.post<ProgEvent[]>(
      '/api/groups/events',
      guestIds,
    )
    return eventsToJsFormat(data)
  },

  async getGuestMarkers(guestIds: string[]): Promise<ProgEvent[]> {
    const { data } = await axios.post<ProgEvent[]>(
      '/api/groups/markers',
      guestIds,
    )
    return eventsToJsFormat(data)
  },

  async editGuestsBatch(ids: string[], typeId?: string): Promise<void> {
    await axios.post('/api/groups/batch-edit-show', {
      ids,
      typeId,
    })
  },
}
